import { action, intercept, makeObservable, observable } from 'mobx'
import { IValidationSchema, required, validationContext } from 'validx'
import { BankAccountDetails } from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import { maxLength, mustBeValidTaxFormInputValue } from 'utils/validation-utils'
import {
  bankAccountTypeFromString,
  bankAccountTypeToString,
} from '../taxRefundMethodGrpcDtoMapper'

export type BankAccountTypeOption = 'Checking' | 'Savings'

export default class BankAccountEditorViewModel {
  @observable accountType: BankAccountTypeOption | '' = ''

  @observable bankName = ''

  @observable routingNumber = ''

  @observable accountNumber = ''

  private schema: IValidationSchema<this> = {
    accountType: [required('Account type is required')],
    bankName: [
      mustBeValidTaxFormInputValue(),
      maxLength(100, 'Bank name must not exceed 100 characters'),
    ],
    routingNumber: [required('Routing number is required')],
    accountNumber: [required('Account number is required')],
  }

  validation = validationContext(this)

  constructor() {
    makeObservable(this)

    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  @action.bound
  validate() {
    return this.validation.reset().validate(this.schema).isValid
  }

  @action.bound
  reset(account?: BankAccountDetails.AsObject) {
    const typeLabel = account
      ? bankAccountTypeToString(account.accountType)
      : ''
    this.accountType = typeLabel === '' ? '' : typeLabel
    this.bankName = account?.bankName ?? ''
    this.routingNumber = account?.routingNumber ?? ''
    this.accountNumber = account?.accountNumber ?? ''
    this.validation.reset()
  }

  toBankAccount(): BankAccountDetails.AsObject {
    return {
      accountType: bankAccountTypeFromString(this.accountType),
      bankName: this.bankName,
      routingNumber: this.routingNumber,
      accountNumber: this.accountNumber,
    }
  }
}
